import type { ApolloCache, Reference } from '@apollo/client'
import { ShiftsShiftFragment, ShiftsShiftFragmentDoc } from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'

import {
  appendRefsToEntityField,
  type ViewRange,
} from 'modules/shift_management/utils/apolloCache/appendRefsToEntityField'

type ShiftsFieldName = 'shifts' | 'shiftsWithoutWorkArea'

const includesShiftEmployee = (
  args: Record<string, unknown> | null,
  shift: ShiftsShiftFragment
) => {
  const employeeIds = args?.employeeIds

  if (!Array.isArray(employeeIds) || employeeIds.length === 0) return true

  return employeeIds.some((employeeId) => String(employeeId) === shift.employeeId)
}

const writeShiftRefs = (cache: ApolloCache<unknown>, shifts: ShiftsShiftFragment[]) => {
  const refsById = new Map<string, Reference>()

  shifts.forEach((shift) => {
    const newRef = cache.writeFragment({
      data: shift,
      fragment: ShiftsShiftFragmentDoc,
      fragmentName: 'ShiftsShift',
    })

    if (newRef) refsById.set(shift.id, newRef)
  })

  return refsById
}

const appendShiftRefsToEntity = ({
  cache,
  entity,
  fieldName,
  shifts,
  refsById,
  view,
}: {
  cache: ApolloCache<unknown>
  entity: { __typename: string; id: string }
  fieldName: ShiftsFieldName
  shifts: ShiftsShiftFragment[]
  refsById: Map<string, Reference>
  view: ViewRange
}) => {
  if (shifts.length === 0) return

  appendRefsToEntityField({
    cache,
    entity,
    fieldName,
    items: shifts,
    refsById,
    view,
    shouldAppend: (shift, args) => includesShiftEmployee(args, shift),
  })
}

const updateCreatedShiftsCache = ({
  cache,
  view,
  shifts,
}: {
  cache: ApolloCache<unknown>
  view: ViewRange
  shifts: ShiftsShiftFragment[]
}) => {
  const refsById = writeShiftRefs(cache, shifts)

  const shiftsByEmployee = groupBy(shifts, 'employeeId')
  Object.entries(shiftsByEmployee).forEach(([employeeId, employeeShifts]) => {
    appendShiftRefsToEntity({
      cache,
      entity: { __typename: 'ShiftManagementEmployeesPermission', id: employeeId },
      fieldName: 'shifts',
      shifts: employeeShifts,
      refsById,
      view,
    })
  })

  const shiftsByWorkArea = groupBy(
    shifts.filter((shift) => shift.locationsWorkAreaId),
    'locationsWorkAreaId'
  )
  Object.entries(shiftsByWorkArea).forEach(([workAreaId, workAreaShifts]) => {
    appendShiftRefsToEntity({
      cache,
      entity: { __typename: 'ShiftManagementWorkArea', id: workAreaId },
      fieldName: 'shifts',
      shifts: workAreaShifts,
      refsById,
      view,
    })
  })

  const shiftsWithoutWorkAreaByLocation = groupBy(
    shifts.filter((shift) => !shift.locationsWorkAreaId && shift.locationId),
    'locationId'
  )
  Object.entries(shiftsWithoutWorkAreaByLocation).forEach(([locationId, locationShifts]) => {
    appendShiftRefsToEntity({
      cache,
      entity: { __typename: 'LocationsLocation', id: locationId },
      fieldName: 'shiftsWithoutWorkArea',
      shifts: locationShifts,
      refsById,
      view,
    })
  })
}

export { updateCreatedShiftsCache }
